<!-- ============================================================
  ExpiryRequestList.vue (<expiry-request-list>) — คำขอเลื่อนวันหมดอายุคอร์ส
  ออกแบบ: C:\iStarApp\COURSE_EXPIRY_ADJUST_DESIGN.md

  • สองมุมมองในกล่องเดียว (ไม่ซ้อน modal): รายการคำขอ ↔ รายละเอียดคำขอ
  • ผู้จัดการ: เห็นทุกคำขอ + ตัดสินรายใบ/ทั้งคำขอ (backend บังคับสิทธิ์ซ้ำ) · แอดมิน: เห็นของตัวเอง + ยกเลิกได้
  • รายละเอียดรายใบ: ประเภทคอร์ส · จำนวนวัน/ครั้ง · คงเหลือ · ใครใช้ · สลิปที่แนบมา (ป้าย "ใหม่") กดดูภาพได้
============================================================ -->
<template>
  <id-modal :model-value="modelValue" size="xl" icon="mdi-calendar-clock" :title="title" :persistent="!!detail || !!viewer"
    @update:model-value="v => $emit('update:modelValue', v)">
    <!-- ── มุมมองรายการ ── -->
    <template v-if="!detail">
      <div v-if="loading" class="er-loading"><span v-if="slow">{{ $t('common.loading') }}</span></div>
      <div v-else-if="!requests.length" class="grid-empty"><span class="mdi mdi-calendar-blank-outline"></span>{{ $t('expiryReq.noRequests') }}</div>
      <div v-else class="erl-list">
        <button v-for="r in requests" :key="r.requestid" class="erl-item" @click="open(r.requestid)">
          <div class="erl-top">
            <b>#{{ r.requestid }}</b>
            <span class="badge" :class="statusCls(r.status)">{{ $t('expiryReq.status.' + r.status) }}</span>
            <span v-if="r.slipCount > 0" class="badge badge-primary"><span class="mdi mdi-paperclip"></span> {{ $t('expiryReq.hasSlip', { n: r.slipCount }) }}</span>
            <span class="erl-meta">{{ r.itemCount }} {{ $t('expiryReq.courses') }} · {{ r.createby }} · {{ $formatDateTime(r.createdate) }}</span>
          </div>
          <div class="erl-reason">{{ r.reason }}</div>
        </button>
      </div>
    </template>

    <!-- ── มุมมองรายละเอียด ── -->
    <template v-else>
      <button class="id-btn id-btn-ghost id-btn-sm" @click="back"><span class="mdi mdi-arrow-left"></span> {{ $t('expiryReq.backToList') }}</button>
      <div class="erd-head">
        <b>#{{ detail.request.requestid }}</b>
        <span class="badge" :class="statusCls(detail.request.status)">{{ $t('expiryReq.status.' + detail.request.status) }}</span>
        <span class="erl-meta">{{ detail.request.createby }} · {{ $formatDateTime(detail.request.createdate) }}</span>
      </div>
      <div class="erd-reason">
        <div class="erd-reason-lbl"><span class="mdi mdi-text-box-outline"></span> {{ $t('expiryReq.requestReason') }}</div>
        <div class="erd-reason-txt">{{ detail.request.reason }}</div>
      </div>
      <div v-if="detail.request.decide_note" class="t-cap">{{ $t('expiryReq.decideNote') }}: {{ detail.request.decide_note }}</div>

      <!-- เลือกทั้งหมด + ตัวนับ (ผู้จัดการ · ใบที่ยังรอตัดสิน) -->
      <div v-if="canDecide" class="erd-selbar">
        <label class="erd-selall">
          <input type="checkbox" class="erd-chk" :checked="allSelected" :indeterminate.prop="someSelected && !allSelected" @change="toggleAll">
          {{ $t('expiryReq.selectAll') }}
        </label>
        <span class="erl-meta">{{ $t('expiryReq.selectedCount', { n: selectedCount, total: pendingItems.length }) }}</span>
      </div>

      <div class="erd-items">
        <div v-for="it in detail.items" :key="it.itemid" class="erd-item"
          :class="['is-' + it.item_status, { 'is-selectable': canDecide && it.item_status === 'pending', 'is-selected': selected[it.itemid] }]"
          @click="onRowClick(it)">
          <input v-if="canDecide && it.item_status === 'pending'" type="checkbox" class="erd-chk" :checked="!!selected[it.itemid]"
            :aria-label="$t('expiryReq.selectItem', { code: it.courserefer })" @click.stop @change="toggle(it.itemid)">
          <div class="erd-body">
            <div class="erd-line">
              <b>{{ it.courserefer }}</b>
              <span class="t-cap">{{ it.coursename }}</span>
              <span class="badge badge-primary">{{ it.coursetype === 'Monthly' ? $t('home.monthly') : it.coursetype }}</span>
              <span v-if="it.hasNewSlip" class="badge badge-warning"><span class="mdi mdi-paperclip"></span> {{ $t('expiryReq.newSlip') }}</span>
              <span class="badge" :class="itemCls(it.item_status)">{{ $t('expiryReq.itemStatus.' + it.item_status) }}</span>
            </div>
            <div class="erd-dates">
              <span class="t-cap">{{ $t('expiryReq.expiryOld') }}</span>
              {{ fmt(it.old_expiredate) }} <span class="mdi mdi-arrow-right"></span>
              <span class="t-cap">{{ $t('expiryReq.expiryNew') }}</span> <b>{{ fmt(it.new_expiredate) }}</b>
              <span class="badge badge-success">+{{ it.days_added }} {{ $t('expiryReq.days') }}</span>
              <span v-if="it.extra_days" class="t-cap">({{ $t('expiryReq.includesExtra', { n: it.extra_days }) }})</span>
            </div>
            <!-- รายละเอียดคอร์สให้ผู้จัดการตัดสินใจ: ระยะเวลา/จำนวนครั้ง · คงเหลือ · ใครใช้ (ประเภทอยู่ที่ป้ายด้านบนแล้ว) -->
            <div class="erd-facts">
              <span v-if="it.coursetype === 'Monthly' && it.period"><span class="t-cap">{{ $t('customerCourse.period') }}</span> {{ it.period }} {{ $t('expiryReq.months') }}</span>
              <span v-else-if="it.coursetype !== 'Monthly'"><span class="t-cap">{{ $t('expiryReq.sessions') }}</span> {{ it.totalSessions }}</span>
              <span v-if="it.coursetype !== 'Monthly'"><span class="t-cap">{{ $t('table.remaining') }}</span> {{ it.remaining }}</span>
              <span><span class="t-cap">{{ $t('table.startDate') }}</span> {{ fmt(it.startdate) }}</span>
              <span><span class="t-cap">{{ $t('expiryReq.users') }}</span> {{ it.users.length ? it.users.map(u => u.name).join(', ') : '-' }}</span>
            </div>
            <!-- วันหมดอายุของใบเปลี่ยนไปหลังยื่นคำขอ → อนุมัติแล้วจะเลื่อนไม่สำเร็จ (backend กันไม่ให้ทับ) บอกผู้จัดการก่อนกด -->
            <div v-if="it.item_status === 'pending' && it.current_expiredate && it.current_expiredate !== it.old_expiredate" class="erd-err">
              <span class="mdi mdi-alert-outline"></span> {{ $t('expiryReq.expiryDrifted', { date: fmt(it.current_expiredate) }) }}
            </div>
            <div v-if="it.apply_error" class="erd-err"><span class="mdi mdi-alert-circle-outline"></span> {{ it.apply_error }}</div>
            <div v-if="it.slips.length" class="erd-slips">
              <button v-for="s in it.slips" :key="s.slipid" class="erd-thumb-btn" :class="{ 'is-medical': s.kind === 'medical' }" v-hint="s.kind === 'medical' ? $t('expiryReq.medicalTag') : $t('expiryReq.viewSlip')"
                :aria-label="$t('expiryReq.viewSlip')" @click.stop="viewer = s.slip_url"><img :src="s.slip_url" class="erd-thumb" alt=""></button>
            </div>
            <!-- ผลตรวจสลิปที่ตรวจตอนยื่นคำขอ (อนุมัติแล้วไม่ตรวจซ้ำ — สลิปเข้าใบคอร์สพร้อมผลนี้) -->
            <slip-verification-panel v-for="s in it.slips.filter(x => x.kind !== 'medical')" :key="'v' + s.slipid" :verification="s.verify"></slip-verification-panel>
          </div>
        </div>
      </div>

      <div v-if="canDecide" class="field full" style="margin-top:12px">
        <label>{{ $t('expiryReq.noteLabel') }}</label>
        <input class="id-input" v-model="note" maxlength="500" autocomplete="off" name="expiry-decide-note">
        <div class="t-cap" style="margin-top:4px">{{ $t('expiryReq.noteHint') }}</div>
      </div>
    </template>

    <template #footer>
      <template v-if="detail && canDecide">
        <button class="id-btn id-btn-ghost" :disabled="busy || !selectedCount" @click="decideSelected('reject')"><span class="mdi mdi-close"></span> {{ $t('expiryReq.rejectSelected', { n: selectedCount }) }}</button>
        <button class="id-btn id-btn-primary" :disabled="busy || !selectedCount" @click="decideSelected('approve')"><span class="mdi mdi-check-all"></span> {{ $t('expiryReq.approveSelected', { n: selectedCount }) }}</button>
      </template>
      <template v-else-if="detail && canCancel">
        <button class="id-btn id-btn-ghost" :disabled="busy" @click="cancel">{{ $t('expiryReq.cancelRequest') }}</button>
        <button v-if="canEdit" class="id-btn id-btn-primary" :disabled="busy" @click="$emit('edit', detail)">
          <span class="mdi mdi-pencil"></span> {{ $t('expiryReq.editRequest') }}
        </button>
      </template>
      <button v-if="!detail" class="id-btn id-btn-ghost" @click="$emit('update:modelValue', false)">{{ $t('btn.close') }}</button>
    </template>
  </id-modal>

  <id-modal :model-value="!!viewer" size="lg" icon="mdi-image" :title="$t('expiryReq.viewSlip')" @update:model-value="v => { if (!v) viewer = null; }">
    <img v-if="viewer" :src="viewer" class="erd-big" alt="">
  </id-modal>
</template>

<script>
import axios from 'axios';

export default {
  name: 'ExpiryRequestList',
  props: { modelValue: { type: Boolean, default: false }, isHead: { type: Boolean, default: false } },
  emits: ['update:modelValue', 'changed', 'edit', 'onLoading', 'onErrorHandler', 'onInfoHandler'],
  data: () => ({
    requests: [], loading: false, slow: false, detail: null, selected: {}, note: '', busy: false, viewer: null, slowTimer: null,
  }),
  computed: {
    token() { return this.$store.getters.getToken; },
    title() { return this.detail ? this.$t('expiryReq.detailTitle') : this.$t('expiryReq.listTitle'); },
    hasPending() { return !!this.detail && this.detail.items.some((i) => i.item_status === 'pending'); },
    canDecide() { return this.isHead && this.detail && this.detail.request.status === 'pending' && this.hasPending; },
    // แก้ไขได้: ผู้ยื่นเอง + ยังรออนุมัติ + ยังไม่มีรายการไหนถูกตัดสิน (backend บังคับซ้ำ)
    canEdit() {
      const u = this.$store.getters.getUserdata;
      return !!this.detail && this.detail.request.status === 'pending' && !!u && u.username === this.detail.request.createby
        && this.detail.items.every((i) => i.item_status === 'pending');
    },
    canCancel() { return this.detail && this.detail.request.status === 'pending'; },
    pendingItems() { return this.detail ? this.detail.items.filter((i) => i.item_status === 'pending') : []; },
    selectedCount() { return this.pendingItems.filter((i) => this.selected[i.itemid]).length; },
    allSelected() { return this.pendingItems.length > 0 && this.selectedCount === this.pendingItems.length; },
    someSelected() { return this.selectedCount > 0; },
  },
  watch: {
    modelValue(open) { if (open) { this.detail = null; this.viewer = null; this.load(); } },
  },
  methods: {
    fmt(d) { return d ? this.$formatShortDate(d) : '-'; },
    // ลากเลือกข้อความในการ์ด (คัดลอกรหัส/ชื่อ) ต้องไม่ไปสลับการเลือกแถว
    onRowClick(it) {
      if (!this.canDecide || it.item_status !== 'pending') return;
      const sel = window.getSelection && window.getSelection().toString();
      if (sel) return;
      this.toggle(it.itemid);
    },
    toggle(itemid) { this.selected = { ...this.selected, [itemid]: !this.selected[itemid] }; },
    toggleAll() {
      const on = !this.allSelected;
      this.selected = Object.fromEntries(this.pendingItems.map((i) => [i.itemid, on]));
    },
    statusCls(s) { return { pending: 'badge-warning', approved: 'badge-success', partial: 'badge-primary', rejected: 'badge-error', failed: 'badge-error', cancelled: '' }[s] || ''; },
    itemCls(s) { return { pending: 'badge-warning', approved: 'badge-success', rejected: 'badge-error', failed: 'badge-error', cancelled: '' }[s] || ''; },
    headers() { return { headers: { Authorization: 'Bearer ' + this.token } }; },
    fail(e) { this.$emit('onErrorHandler', (e.response && e.response.data && e.response.data.message) || e.message); },
    async load() {
      this.loading = true; this.slow = false;
      clearTimeout(this.slowTimer);
      this.slowTimer = setTimeout(() => { this.slow = true; }, 400);
      try {
        const res = await axios.post(this.baseURL + '/getExpiryRequests', {}, this.headers());
        this.requests = res.data.success ? res.data.requests : [];
      } catch (e) { this.fail(e); } finally { clearTimeout(this.slowTimer); this.loading = false; }
    },
    async open(requestid) {
      this.$emit('onLoading', true);
      try {
        const res = await axios.post(this.baseURL + '/getExpiryRequestDetail', { requestid }, this.headers());
        if (!res.data.success) { this.$emit('onErrorHandler', res.data.message); return; }
        this.selected = {}; this.note = '';
        this.detail = res.data;
      } catch (e) { this.fail(e); } finally { this.$emit('onLoading', false); }
    },
    async back() { this.detail = null; await this.load(); },
    async send(path, body) {
      this.busy = true; this.$emit('onLoading', true);
      try {
        const res = await axios.post(this.baseURL + path, body, this.headers());
        if (!res.data.success) { this.$emit('onErrorHandler', res.data.message); return false; }
        return res.data;
      } catch (e) { this.fail(e); return false; } finally { this.busy = false; this.$emit('onLoading', false); }
    },
    async afterDecision(out) {
      this.$emit('changed');
      const failed = (out.results || []).filter((r) => r.status === 'failed');
      this.$emit('onInfoHandler', failed.length
        ? this.$t('expiryReq.decidedWithFail', { n: failed.length }) : this.$t('expiryReq.decided'));
      this.detail = null;
      await this.load();
    },
    // ทำกับรายการที่ติ๊กเท่านั้น (อนุมัติ/ไม่อนุมัติ) — ที่เหลือยังรอ คำขอจะจบเมื่อทุกรายการถูกตัดสินแล้ว
    async decideSelected(action) {
      const decisions = this.pendingItems.filter((i) => this.selected[i.itemid]).map((i) => ({ itemid: i.itemid, action }));
      if (!decisions.length) return;
      const out = await this.send('/decideExpiryRequest', { requestid: this.detail.request.requestid, decisions, note: this.note });
      if (out) await this.afterDecision(out);
    },
    async cancel() {
      const out = await this.send('/cancelExpiryRequest', { requestid: this.detail.request.requestid });
      if (out) { this.$emit('changed'); this.detail = null; await this.load(); }
    },
  },
  beforeUnmount() { clearTimeout(this.slowTimer); },
};
</script>

<style scoped>
.er-loading { min-height: 80px; display: flex; align-items: center; justify-content: center; color: var(--c-text-2, #888); }
.erl-list { display: flex; flex-direction: column; gap: 8px; }
.erl-item { text-align: left; border: 1px solid var(--c-border, #eee); border-radius: 12px; padding: 12px; background: var(--c-surface, #fff); cursor: pointer; font: inherit; color: inherit; }
.erl-item:hover { border-color: var(--c-primary); }
.erl-top, .erd-head, .erd-line, .erd-dates { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
.erl-meta { color: var(--c-text-2, #888); font-size: 12px; }
.erl-reason, .erd-reason { margin-top: 6px; }
.erd-reason { padding: 10px 12px; border-radius: 10px; background: var(--c-primary-soft); }
.erd-reason-lbl { font-size: 12px; font-weight: 700; opacity: .75; }
.erd-reason-txt { margin-top: 2px; font-size: 15px; }
.erd-selbar { margin-top: 12px; display: flex; align-items: center; gap: 12px; padding: 8px 12px; border-radius: 10px; background: var(--c-surface-2, rgba(0,0,0,.04)); }
.erd-selall { display: flex; align-items: center; gap: 8px; font-weight: 700; cursor: pointer; }
.erd-chk { width: 20px; height: 20px; accent-color: var(--c-primary); cursor: pointer; flex: 0 0 auto; }
.erd-item.is-selectable { cursor: pointer; }
.erd-item.is-selectable:hover { border-color: var(--c-primary); }
.erd-item.is-selected { border-color: var(--c-primary); background: var(--c-primary-soft); }
.erd-body { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 6px; }
.erd-head { margin-top: 12px; }
.erd-items { margin-top: 12px; display: flex; flex-direction: column; gap: 10px; }
.erd-item { border: 1px solid var(--c-border, #eee); border-radius: 12px; padding: 12px; display: flex; flex-direction: row; align-items: flex-start; gap: 12px; background: var(--c-surface, #fff); }
.erd-item.is-approved { border-color: var(--c-success); }
.erd-item.is-rejected, .erd-item.is-failed { border-color: var(--c-error); }
.erd-facts { display: flex; gap: 6px 18px; flex-wrap: wrap; font-size: 13px; }
.erd-facts .t-cap { margin-right: 4px; }
.erd-err { color: var(--c-error-tx); font-size: 13px; }
.erd-thumb-btn.is-medical { outline: 2px solid var(--c-info, #3b82f6); outline-offset: 1px; border-radius: 6px; }
.erd-slips { display: flex; gap: 8px; flex-wrap: wrap; }
.erd-thumb-btn { border: none; background: transparent; padding: 0; cursor: zoom-in; }
.erd-thumb { width: 72px; height: 72px; object-fit: cover; border-radius: 8px; border: 1px solid var(--c-border, #eee); cursor: zoom-in; }
.erd-big { max-width: 100%; max-height: 70vh; display: block; margin: 0 auto; border-radius: 8px; }
</style>
